import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { useAuth } from '../contexts/AuthContext'
import { useTenantModulos } from '../hooks/useTenantModulos'
import type { NavSection } from '../navigation/navigation.config'
import { setCommitteeView } from '../navigation/navState'
import { useNavBadges } from '../navigation/useNavBadges'
import { useNavigation, type ResolvedModule } from '../navigation/useNavigation'
import { NAVBAR_HEIGHT } from './Navbar'
import PropertySwitcher from './PropertySwitcher'
import indlusFavicon from '../../assets/indlusfavico.png'

export const SIDEBAR_WIDTH = 264
/** Menu recolhido: barra de ícones com tooltip, um ícone por módulo. */
export const SIDEBAR_RAIL_WIDTH = 72

const MANAGEMENT_SECTIONS: NavSection[] = ['gestao', 'comunidade', 'administracao']

interface Props {
    mobileOpen: boolean
    onClose: () => void
    collapsed?: boolean
    onToggleCollapse?: () => void
}

export default function Sidebar({ mobileOpen, onClose, collapsed = false, onToggleCollapse }: Props) {
    const { t } = useTranslation(['nav', 'rentals'])
    const location = useLocation()
    const { hasPermission } = useAuth()
    const { temModulo } = useTenantModulos()
    const nav = useNavigation()
    const active = nav.findActive(location.pathname)
    const [moreOpen, setMoreOpen] = useState(() => !!active && nav.moreModules.includes(active.module))
    const badges = useNavBadges(nav.condominioId, nav.profile === 'gestao' && hasPermission('FINANCAS_READ') && temModulo('FINANCAS'))

    const badgeFor = (m: ResolvedModule) => (m.badge === 'financeOverdueUnits' ? badges.financeOverdueUnits ?? 0 : 0)

    const renderItem = (m: ResolvedModule, rail: boolean) => {
        const selected = active?.module === m
        const Icon = m.icon
        const count = badgeFor(m)
        const label = t(m.label)
        const badgeLabel = count > 0 ? t('nav:badges.financeOverdue', { count }) : undefined
        const button = (
            <ListItemButton
                key={`${m.section}:${m.id}`}
                component={RouterLink}
                to={m.href}
                onClick={onClose}
                selected={selected}
                aria-current={selected ? 'page' : undefined}
                aria-label={rail ? [label, badgeLabel].filter(Boolean).join(' · ') : undefined}
                sx={{
                    borderRadius: '10px',
                    mb: 0.25,
                    minHeight: 44,
                    px: rail ? 0 : 1.5,
                    justifyContent: rail ? 'center' : 'flex-start',
                    color: 'text.primary',
                    '&.Mui-selected': {
                        bgcolor: 'brand.teal50',
                        color: 'primary.main',
                        '& .MuiListItemIcon-root': { color: 'brand.teal700' },
                        '&:hover': { bgcolor: 'brand.teal50' },
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: rail ? 0 : 36, color: 'text.secondary', justifyContent: 'center' }}>
                    {rail && count > 0
                        ? <Badge variant="dot" color="warning"><Icon fontSize="small" /></Badge>
                        : <Icon fontSize="small" />}
                </ListItemIcon>
                {!rail && (
                    <ListItemText
                        primary={label}
                        slotProps={{ primary: { sx: { fontWeight: selected ? 800 : 600, fontSize: '0.875rem' }, noWrap: true } }}
                    />
                )}
                {!rail && count > 0 && (
                    <Box
                        component="span"
                        title={badgeLabel}
                        aria-label={badgeLabel}
                        sx={{ ml: 1, px: 0.875, minWidth: 22, height: 22, borderRadius: '11px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'status.warning.bg', color: 'status.warning.fg', fontSize: '0.75rem', fontWeight: 800 }}
                    >
                        {count}
                    </Box>
                )}
            </ListItemButton>
        )
        return rail
            ? <Tooltip key={`${m.section}:${m.id}`} title={badgeLabel ? `${label} · ${badgeLabel}` : label} placement="right">{button}</Tooltip>
            : button
    }

    const sectionHeader = (section: NavSection, rail: boolean) => rail
        ? <Divider key={`h-${section}`} sx={{ my: 1, mx: 1.5 }} />
        : (
            <Typography key={`h-${section}`} variant="caption" component="p" sx={{ px: 1.5, pt: 2, pb: 0.75, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t(`nav:sections.${section}`)}
            </Typography>
        )

    const content = (rail: boolean) => (
        <Box sx={{ width: rail ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
            {/* Cabeçalho do drawer móvel — indicação explícita de como fechar o menu. */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2">{t('nav:sidebar.menu')}</Typography>
                <IconButton onClick={onClose} aria-label={t('nav:sidebar.closeMenu')}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: rail ? 1 : 1.5, pt: 1.5, pb: 0.5 }}>
                <PropertySwitcher collapsed={rail} />
            </Box>

            <Box component="nav" aria-label={t('nav:sidebar.mainNav')} sx={{ flexGrow: 1, overflowY: 'auto', px: rail ? 1 : 1.5, pb: 2 }}>
                {nav.profile === 'gestao' ? (
                    MANAGEMENT_SECTIONS.map((section) => {
                        const items = nav.modules.filter((m) => m.section === section)
                        if (items.length === 0) return null
                        return (
                            <Box key={section}>
                                {sectionHeader(section, rail)}
                                <List disablePadding>{items.map((m) => renderItem(m, rail))}</List>
                            </Box>
                        )
                    })
                ) : (
                    <>
                        <List disablePadding sx={{ pt: 1 }}>{nav.modules.map((m) => renderItem(m, rail))}</List>

                        {nav.isCommitteeMember && !rail && (
                            <Box sx={{ mt: 1, px: 1.5, py: 1, borderRadius: '10px', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }} id="committee-view-label">{t('nav:resident.committeeView')}</Typography>
                                    <Typography variant="caption" color="text.secondary">{t('nav:resident.committeeViewHint')}</Typography>
                                </Box>
                                <Switch
                                    checked={nav.committeeView}
                                    onChange={(_, checked) => setCommitteeView(checked)}
                                    slotProps={{ input: { 'aria-labelledby': 'committee-view-label' } }}
                                />
                            </Box>
                        )}

                        {nav.moreModules.length > 0 && (rail ? (
                            <>
                                {sectionHeader('mais', true)}
                                <List disablePadding>{nav.moreModules.map((m) => renderItem(m, true))}</List>
                            </>
                        ) : (
                            <>
                                <ListItemButton onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen} sx={{ borderRadius: '10px', mt: 1, minHeight: 44, px: 1.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}><MoreHorizRoundedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary={t('nav:sections.mais')} slotProps={{ primary: { sx: { fontWeight: 600, fontSize: '0.875rem' } } }} />
                                    {moreOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </ListItemButton>
                                <Collapse in={moreOpen} timeout="auto">
                                    <List disablePadding sx={{ pl: 1 }}>{nav.moreModules.map((m) => renderItem(m, false))}</List>
                                </Collapse>
                            </>
                        ))}
                    </>
                )}
            </Box>

            <Divider />
            <Box sx={{ px: rail ? 1 : 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: rail ? 'center' : 'space-between' }}>
                {!rail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Box component="img" src={indlusFavicon} alt="" sx={{ height: 16, width: 'auto', display: 'block', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {t('nav:sidebar.footer', { year: new Date().getFullYear() })}
                        </Typography>
                    </Box>
                )}
                {onToggleCollapse && (
                    <Tooltip title={t(collapsed ? 'nav:sidebar.expand' : 'nav:sidebar.collapse')} placement="right">
                        <IconButton
                            onClick={onToggleCollapse}
                            aria-label={t(collapsed ? 'nav:sidebar.expand' : 'nav:sidebar.collapse')}
                            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                        >
                            {collapsed ? <KeyboardDoubleArrowRightRoundedIcon fontSize="small" /> : <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    )

    return (
        <>
            {/* Mobile: drawer temporário, fecha ao navegar ou tocar fora */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: SIDEBAR_WIDTH,
                        boxSizing: 'border-box',
                        // Sem isto, o painel fica escondido atrás da Navbar fixa (zIndex superior ao do Drawer).
                        top: NAVBAR_HEIGHT,
                        height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
                    },
                }}
            >
                {content(false)}
            </Drawer>

            {/* Desktop: sempre visível — expandido ou em barra de ícones */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': {
                        width: collapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        top: NAVBAR_HEIGHT,
                        height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
                        overflowX: 'hidden',
                        transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
                    },
                }}
            >
                {content(collapsed)}
            </Drawer>
        </>
    )
}
